/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo } from "react";
import { X, Users, Filter, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  useLeague,
  useLeagueTeams,
  useLeagueGameweekLeaderboard,
} from "@/hooks/api/useLeagues";
import { useGameweeks } from "@/hooks/api/useGameweeks";
import TeamPlayersModal from "@/pages/leagues/TeamPlayersModal";
import type { Gameweek } from "@/types/api.types";

interface Props {
  leagueId: number;
  onClose: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function downloadCSV(rows: any[], filename: string) {
  if (!rows.length) return;
  const headers = ["Rank", "User", "Username", "Team", "Formation", "Points"];
  const csvRows = [
    headers.join(","),
    ...rows.map((r: any) =>
      [
        r.rank,
        `"${r.userName || ""}"`,
        `"${r.username || ""}"`,
        `"${r.team?.name || "No team"}"`,
        r.team?.formation || "",
        r.points ?? r.team?.totalPoints ?? 0,
      ].join(","),
    ),
  ];
  const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function LeagueDetailDrawer({ leagueId, onClose }: Props) {
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [selectedGameweekId, setSelectedGameweekId] = useState<number | null>(
    null,
  );

  const { data: league } = useLeague(leagueId);
  const { data: allTimeData, isLoading: allTimeLoading } =
    useLeagueTeams(leagueId);
  const allTimeTeams = allTimeData?.teams;
  const hasCompletedGameweeks = allTimeData?.hasCompletedGameweeks ?? true;
  const { data: gwLeaderboard, isLoading: gwLoading } =
    useLeagueGameweekLeaderboard(
      leagueId,
      selectedGameweekId ?? 0,
    );
  const { data: gameweeks } = useGameweeks();

  // Only show non-UPCOMING gameweeks
  const sortedGameweeks = useMemo(
    () =>
      [...(gameweeks ?? [])].filter(
        (gw: Gameweek) => gw.status !== "UPCOMING",
      ),
    [gameweeks],
  );

  const selectedGw = sortedGameweeks.find(
    (gw: Gameweek) => gw.id === selectedGameweekId,
  );

  const isLoading = selectedGameweekId ? gwLoading : allTimeLoading;

  // Normalize data: both views produce the same row shape
  const rows: any[] = useMemo(() => {
    if (selectedGameweekId && gwLeaderboard) {
      return gwLeaderboard;
    }
    return allTimeTeams ?? [];
  }, [selectedGameweekId, gwLeaderboard, allTimeTeams]);

  const csvFilename = selectedGw
    ? `league-${league?.name}-${selectedGw.name}.csv`
    : `league-${league?.name}-all-time.csv`;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {league?.name || "League"}
            </h2>
            <div className="flex items-center gap-3 mt-1">
              <code className="text-xs bg-gray-200 px-2 py-0.5 rounded font-mono font-bold tracking-widest">
                {league?.code}
              </code>
              <span className="text-xs text-gray-500">
                <Users className="h-3 w-3 inline mr-1" />
                {league?._count?.members} members
              </span>
              <span className="text-xs text-gray-500">
                by {league?.creator?.name}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Gameweek Filter */}
        <div className="px-6 py-3 border-b bg-white">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Filter className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Filter by Matchweek
              </span>
            </div>
            <button
              onClick={() => downloadCSV(rows, csvFilename)}
              disabled={!rows.length}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition disabled:opacity-40"
            >
              <Download className="h-3.5 w-3.5" />
              CSV
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setSelectedGameweekId(null)}
              className={`px-3 py-1.5 text-xs rounded-full font-medium transition ${
                !selectedGameweekId
                  ? "bg-blue-600 text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
              }`}
            >
              All Time
            </button>
            {sortedGameweeks.map((gw: Gameweek) => (
              <button
                key={gw.id}
                onClick={() => setSelectedGameweekId(gw.id)}
                className={`px-3 py-1.5 text-xs rounded-full font-medium transition ${
                  selectedGameweekId === gw.id
                    ? "bg-blue-600 text-white"
                    : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                }`}
              >
                {gw.name}
              </button>
            ))}
          </div>
        </div>

        {/* Teams Table */}
        <div className="flex-1 overflow-y-auto">
          {/* Pre-season banner */}
          {!isLoading && !hasCompletedGameweeks && !selectedGameweekId && rows.length > 0 && (
            <div className="mx-6 mt-3 mb-1 flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              <span>⏳</span>
              <span>Rankings will update after the first matchday is completed.</span>
            </div>
          )}

          {/* Column Headers */}
          <div className="grid grid-cols-12 gap-3 px-6 py-3 bg-gray-50 border-b text-xs font-semibold text-gray-500 uppercase tracking-wide sticky top-0">
            <div className="col-span-1 text-center">Rank</div>
            <div className="col-span-4">User</div>
            <div className="col-span-2">Team</div>
            <div className="col-span-2 text-center">Formation</div>
            <div className="col-span-1 text-center">
              {selectedGameweekId ? "GW" : "Players"}
            </div>
            <div className="col-span-2 text-right">Points</div>
          </div>

          {isLoading ? (
            [...Array(5)].map((_, i) => (
              <div
                key={i}
                className="grid grid-cols-12 gap-3 px-6 py-4 border-b animate-pulse"
              >
                {[...Array(5)].map((_, j) => (
                  <div
                    key={j}
                    className="col-span-2 h-4 bg-gray-200 rounded"
                  />
                ))}
              </div>
            ))
          ) : !rows.length ? (
            <div className="text-center py-12 text-gray-400">
              <Users className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>
                {selectedGameweekId
                  ? "No data for this matchweek"
                  : "No members yet"}
              </p>
            </div>
          ) : (
            rows.map((entry: any) => {
              const points = selectedGameweekId
                ? entry.points ?? 0
                : entry.team?.totalPoints ?? 0;

              return (
                <div
                  key={entry.memberId || entry.userId}
                  className={`grid grid-cols-12 gap-3 px-6 py-4 border-b last:border-0 items-center hover:bg-gray-50 transition ${
                    hasCompletedGameweeks && entry.rank <= 3 ? "bg-yellow-50/30" : ""
                  }`}
                >
                  {/* Rank */}
                  <div className="col-span-1 text-center">
                    {!hasCompletedGameweeks && !selectedGameweekId ? (
                      <span className="text-sm font-bold text-gray-300">—</span>
                    ) : entry.rank === 1 ? (
                      <span className="text-lg">🥇</span>
                    ) : entry.rank === 2 ? (
                      <span className="text-lg">🥈</span>
                    ) : entry.rank === 3 ? (
                      <span className="text-lg">🥉</span>
                    ) : (
                      <span className="text-sm font-bold text-gray-400">
                        #{entry.rank}
                      </span>
                    )}
                  </div>

                  {/* User */}
                  <div className="col-span-4 flex items-center gap-2 min-w-0">
                    {entry.avatar ? (
                      <img
                        src={entry.avatar}
                        className="w-8 h-8 rounded-full shrink-0"
                        alt=""
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                        {(entry.userName || "?").charAt(0)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p
                        className="text-sm font-medium text-gray-900 truncate"
                        title={entry.userName}
                      >
                        {entry.userName}
                      </p>
                      <p
                        className="text-xs text-gray-400 truncate"
                        title={`@${entry.username}`}
                      >
                        @{entry.username}
                      </p>
                    </div>
                  </div>

                  {/* Team */}
                  <div className="col-span-2 min-w-0">
                    {entry.team ? (
                      <button
                        onClick={() => setSelectedTeamId(entry.team.id)}
                        className="block w-full text-sm font-semibold text-blue-600 hover:underline text-left truncate"
                        title={entry.team.name}
                      >
                        {entry.team.name}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400 italic">
                        No team
                      </span>
                    )}
                  </div>

                  {/* Formation */}
                  <div className="col-span-2 text-center">
                    {entry.team && (
                      <Badge variant="outline" className="text-xs">
                        {entry.team.formation}
                      </Badge>
                    )}
                  </div>

                  {/* Players / GW indicator */}
                  <div className="col-span-1 text-center text-sm text-gray-600">
                    {selectedGameweekId
                      ? entry.totalPoints != null
                        ? entry.totalPoints
                        : "\u2014"
                      : entry.team?.playerCount ?? "\u2014"}
                  </div>

                  {/* Points */}
                  <div className="col-span-2 text-right">
                    <span className="text-lg font-black text-gray-900">
                      {points}
                    </span>
                    <span className="text-xs text-gray-400 ml-1">pts</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Team Players Modal */}
      {selectedTeamId && (
        <TeamPlayersModal
          teamId={selectedTeamId}
          gameweekId={selectedGameweekId ?? undefined}
          onClose={() => setSelectedTeamId(null)}
        />
      )}
    </>
  );
}
